import { ELoadingStatus } from "@/app/core/models/loading-status.model";
import { loadCurrentUser } from "@/app/stores/auth/auth.actions";
import { selectorCurrentUser, selectorCurrentUserStatus } from "@/app/stores/auth/auth.selectors";
import { inject } from "@angular/core";
import { CanActivateFn, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { filter, switchMap, take, map } from "rxjs/operators";
import { of } from "rxjs";
import { AuthService } from "@auth0/auth0-angular";
import { resolveCaseOnboardingPath } from "@/app/core/utils/case-workflow.util";

/**
 * Guard that prevents authenticated users who already have a business AND verified email
 * from accessing first-time-only pages (onboarding, verification-sent, welcome).
 * Redirects them to /select-business instead.
 *
 */
export const firstTimeGuard: CanActivateFn = (_route, state) => {
  const store = inject(Store);
  const router = inject(Router);
  const auth = inject(AuthService);

  return auth.isAuthenticated$.pipe(
    filter((isAuth) => isAuth !== null),
    take(1),
    switchMap((isAuthenticated) => {
      // Not authenticated → allow (AuthGuard on the route will handle redirect to login)
      if (!isAuthenticated) return of(true);

      return store.select(selectorCurrentUserStatus).pipe(
        take(1),
        switchMap((status) => {
          if (status === ELoadingStatus.idle) {
            store.dispatch(loadCurrentUser());
          }

          return store.select(selectorCurrentUserStatus).pipe(
            filter((s) => s === ELoadingStatus.loaded || s === ELoadingStatus.error),
            take(1),
            switchMap(() => store.select(selectorCurrentUser).pipe(take(1)))
          );
        }),
        map((user) => {
          // User is fully set up (has business + verified email) → redirect away
          if (user?.businessUsers?.length && user?.emailVerified) {
            return router.createUrlTree(["/select-business"]);
          }

          const url = state.url;
          if (!user?.businessUsers?.length && (url.includes("verification-sent") || url.includes("verify-email"))) {
            return router.parseUrl(resolveCaseOnboardingPath());
          }

          // Still in onboarding flow → allow access
          return true;
        })
      );
    })
  );
};
